"use client";

import { Loader, Modal } from "@mantine/core";
import { EmptyState, Icon, MainButton } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { purchaseEsim, topUpEsim } from "@/lib/actions/esim";
import { useServiceReview } from "@/lib/context/ServiceReviewContext";
import type { EsimCurrency, EsimOrder, EsimProduct } from "@/lib/types/esim";
import { planDataAllowance, validityUnitKey } from "@/lib/utils/esim";

import { EsimInstall } from "./EsimInstall";
import { useEsimDelivery } from "./useEsimDelivery";

/**
 * The only Global API errors worth a message of their own. Everything else is
 * operator-facing detail the customer can do nothing with, so it falls through
 * to the generic line and the console.
 */
const ERROR_KEYS: Record<string, string> = {
  // Not "check your connection and try again": by the time this fires the
  // Global API may already have placed the order.
  "network-error": "error-indeterminate",
  "esim-allowance-exceeded": "error-allowance",
  "esim-not-found": "error-generic",
  // The API's `code` on a failed order (GAPI-968).
  "esim-plan-unavailable": "error-plan-unavailable",
  "esim-order-not-placed": "error-not-placed",
  "esim-order-unconfirmed": "error-unconfirmed",
};

/**
 * Failures where the order may in fact have been placed — the request timed out
 * or the connection dropped after the Global API had already reached DT One, or
 * the API reached DT One and lost the answer (`esim-order-unconfirmed`).
 *
 * The customer must not be offered a retry button here: the first attempt can
 * still complete on the callback, and buying again issues and charges a second
 * eSIM. Everything else (allowance, a rejected product) is a definite no and is
 * safe to try again.
 */
const INDETERMINATE = new Set(["network-error", "esim-order-unconfirmed"]);

type OrderModalProps = {
  product: EsimProduct | null;
  currency: EsimCurrency;
  /** Set to recharge an existing eSIM instead of issuing a new one. */
  iccid?: string;
  onClose: () => void;
  onCompleted: () => void;
};

/**
 * Confirm → submit → wait for DT One → install.
 *
 * Purchase and top-up share this because they differ only in whether an ICCID
 * is sent: the Global API treats a transaction with one as a recharge and a
 * transaction without one as a brand new (and separately charged) eSIM.
 */
export function OrderModal({
  product,
  currency,
  iccid,
  onClose,
  onCompleted,
}: OrderModalProps) {
  const t = useTranslations("esim");
  const [submitting, setSubmitting] = useState(false);
  /** Set when a failure might still have placed the order — blocks the retry. */
  const [locked, setLocked] = useState(false);
  const [submitted, setSubmitted] = useState<EsimOrder | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { order, phase } = useEsimDelivery(submitted);
  const isTopUp = Boolean(iccid);
  const askForReview = useServiceReview();

  const close = () => {
    // GD-120 — on closing rather than at "ready", so the stars don't cover the
    // install steps.
    if (phase === "ready") askForReview("esim");
    setSubmitting(false);
    setLocked(false);
    setSubmitted(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!product) return;

    setSubmitting(true);
    setError(null);

    const result = iccid
      ? await topUpEsim(currency, product.id, iccid)
      : await purchaseEsim(currency, product.id);

    setSubmitting(false);

    if (!result.success) {
      // DT One's own message is operator-facing and untranslated, so it goes to
      // the console for support rather than in front of the customer.
      console.error("[esim] order failed:", result.error);
      setError(ERROR_KEYS[result.error] ?? "error-generic");
      // No retry offered when we cannot tell whether the money moved.
      setLocked(INDETERMINATE.has(result.error));
      return;
    }

    setSubmitted(result.data);
    onCompleted();
  };

  return (
    <Modal
      opened={Boolean(product)}
      onClose={close}
      title={isTopUp ? t("topup-title") : t("purchase-title")}
      size="lg"
      radius="lg"
      centered
    >
      {product && (
        <div className="space-y-5">
          {!submitted && (
            <PlanSummary product={product} isTopUp={isTopUp} error={error} />
          )}

          {submitted && phase === "waiting" && (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Loader size={36} color="var(--shell-primary)" />
              <p className="text-base font-medium text-text-primary">
                {t("delivery-waiting")}
              </p>
              <p className="max-w-sm text-base text-text-secondary">
                {t("delivery-waiting-hint")}
              </p>
            </div>
          )}

          {submitted && phase === "ready" && !isTopUp && order?.profile && (
            <div className="space-y-4">
              <p className="text-base font-medium text-text-primary">
                {t("delivery-ready")}
              </p>
              <EsimInstall profile={order.profile} />
            </div>
          )}

          {submitted && phase === "ready" && isTopUp && (
            <EmptyState
              icon={<Icon name="check" size={28} />}
              title={t("topup-done")}
              description={t("topup-done-description")}
            />
          )}

          {submitted && phase === "timeout" && (
            <EmptyState
              icon={<Icon name="clock" size={28} />}
              title={t("delivery-slow")}
              description={t("delivery-slow-description")}
            />
          )}

          {submitted && phase === "failed" && (
            <EmptyState
              icon={<Icon name="alert" size={28} />}
              title={t("delivery-failed")}
              description={
                order?.status?.error ?? t("delivery-failed-description")
              }
            />
          )}

          <div className="flex justify-end gap-2">
            {!submitted ? (
              <>
                <MainButton variant="secondary" onClick={close}>
                  {t("action-cancel")}
                </MainButton>
                <MainButton onClick={submit} disabled={submitting || locked}>
                  {submitting
                    ? t("action-submitting")
                    : isTopUp
                      ? t("action-confirm-topup")
                      : t("action-confirm-purchase")}
                </MainButton>
              </>
            ) : (
              <MainButton onClick={close}>{t("action-done")}</MainButton>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function PlanSummary({
  product,
  isTopUp,
  error,
}: {
  product: EsimProduct;
  isTopUp: boolean;
  error: string | null;
}) {
  const t = useTranslations("esim");
  const allowance = planDataAllowance(product, t("data-unlimited"));
  const unitKey = product.validity
    ? validityUnitKey(product.validity.unit)
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-content-bg p-4">
        <p className="text-base font-semibold text-text-primary">
          {product.name}
        </p>
        <dl className="mt-3 space-y-2 text-base">
          {product.country && (
            <SummaryRow
              label={t("plan-destination")}
              value={product.country.name}
            />
          )}
          {allowance && <SummaryRow label={t("plan-data")} value={allowance} />}
          {product.validity && (
            <SummaryRow
              label={t("plan-validity")}
              value={
                unitKey
                  ? t(`validity-${unitKey}`, {
                      count: product.validity.quantity,
                    })
                  : `${product.validity.quantity} ${product.validity.unit}`
              }
            />
          )}
          <SummaryRow label={t("plan-price")} value={t("plan-included")} />
        </dl>
      </div>

      {product.usageInfo.length > 0 && (
        <ul className="list-inside list-disc space-y-1 text-base text-text-secondary">
          {product.usageInfo.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      <p className="text-base text-text-secondary">
        {isTopUp ? t("topup-notice") : t("purchase-notice")}
      </p>

      {error && (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-base text-red-700">
          {t(error)}
        </p>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right font-medium text-text-primary">{value}</dd>
    </div>
  );
}
